import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';
import { fetchConfig, debounce } from '@theme/utilities';

const PROP = '_complimentary_sample';
const CLAIMED = 'complimentary-samples-claimed';

class ComplimentarySamplesComponent extends DialogComponent {
  #selected = new Set();
  #busy = false;
  #dismissed = false;
  #check = debounce(() => this.#maybeOpen(), 200);

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCart);
    this.addEventListener('click', this.#onClick);
    this.#check();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCart);
    this.#check.cancel();
  }

  #onCart = (e) => {
    if (e.detail?.data?.source === 'complimentary-samples') return;
    this.#check();
  };

  #onClick = (e) => {
    const btn = e.target.closest('[data-complimentary-sample-product]');
    if (btn && !btn.disabled) {
      e.preventDefault();
      const id = btn.dataset.variantId;
      if (this.#selected.has(id)) this.#selected.delete(id);
      else if (this.#selected.size < this.#max) this.#selected.add(id);
      btn.classList.toggle('is-selected', this.#selected.has(id));
      btn.setAttribute('aria-pressed', String(this.#selected.has(id)));
      this.#updateCounter();
      if (this.#selected.size === this.#max) this.#addSamples();
      return;
    }
    if (e.target.closest('[data-complimentary-samples-close]')) {
      e.preventDefault();
      this.#dismissed = true;
      this.closeDialog();
    }
  };

  async #maybeOpen() {
    if (this.#busy) return;
    const cart = await fetch('/cart.js').then((r) => r.json());
    if (!cart.item_count) {
      sessionStorage.removeItem(CLAIMED);
      this.#dismissed = false;
      return this.closeDialog();
    }
    if (cart.total_price < this.#min) {
      sessionStorage.removeItem(CLAIMED);
      this.#dismissed = false;
      if (this.#hasSamples(cart)) return this.#removeSamples(cart);
      return this.closeDialog();
    }
    if (!this.#hasSamples(cart)) sessionStorage.removeItem(CLAIMED);
    if (this.refs.dialog?.open) return;
    if (this.#hasSamples(cart) || sessionStorage.getItem(CLAIMED) === '1' || this.#dismissed) return;
    this.#selected.clear();
    this.#updateCounter();
    this.#markInCart(cart);
    this.showDialog();
  }

  async #removeSamples(cart) {
    this.#busy = true;
    const updates = Object.fromEntries(cart.items.filter((i) => this.#isSample(i)).map((i) => [i.key, 0]));
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean)
      .join(',');
    try {
      const res = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body: JSON.stringify({ updates, ...(sections && { sections }) }) }),
      }).then((r) => r.json());
      await this.closeDialog();
      const fresh = await fetch('/cart.js').then((r) => r.json());
      document.dispatchEvent(
        new CartAddEvent(fresh, 'complimentary-samples', { source: 'complimentary-samples', sections: res.sections })
      );
    } finally {
      this.#busy = false;
    }
  }

  async #addSamples() {
    if (this.#busy) return;
    this.#busy = true;
    const items = [...this.#selected].map((id) => ({ id: +id, quantity: 1, properties: { [PROP]: 'true' } }));
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean)
      .join(',');
    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', { body: JSON.stringify({ items, ...(sections && { sections }) }) }),
      }).then((r) => r.json());
      if (res.status) {
        document.dispatchEvent(new CartErrorEvent('complimentary-samples', res.message, res.description, res.errors));
        return;
      }
      sessionStorage.setItem(CLAIMED, '1');
      this.#dismissed = false;
      await this.closeDialog();
      const cart = await fetch('/cart.js').then((r) => r.json());
      document.dispatchEvent(new CartAddEvent(cart, 'complimentary-samples', { source: 'complimentary-samples', sections: res.sections }));
      document.querySelector('cart-drawer-component')?.open?.();
    } finally {
      this.#busy = false;
      this.#selected.clear();
      this.#updateCounter();
    }
  }

  #isSample(item) {
    return (
      this.#sampleIds.has(String(item.product_id)) ||
      item.properties?.[PROP] === 'true' ||
      item.properties?.some?.((p) => p.name === PROP && p.value === 'true')
    );
  }

  #hasSamples(cart) {
    return cart.items.some((i) => this.#isSample(i));
  }

  #markInCart(cart) {
    const inCart = new Set(cart.items.filter((i) => this.#isSample(i)).map((i) => String(i.variant_id)));
    this.querySelectorAll('[data-complimentary-sample-product]').forEach((btn) => {
      const taken = inCart.has(btn.dataset.variantId);
      btn.disabled = btn.dataset.available !== 'true' || taken;
      btn.classList.toggle('is-in-cart', taken);
    });
  }

  #updateCounter() {
    const el = this.querySelector('[data-complimentary-samples-counter]');
    if (!el) return;
    const tpl = el.dataset.template || '{selected} of {count} selected';
    el.textContent = tpl.replace('{selected}', String(this.#selected.size)).replace('{count}', String(this.#max));
  }

  get #min() {
    return Number(this.dataset.thresholdCents) || 6000;
  }
  get #max() {
    return Number(this.dataset.maxSamples) || 2;
  }
  get #sampleIds() {
    return new Set((this.dataset.sampleProductIds || '').split(',').filter(Boolean));
  }
}

if (!customElements.get('complimentary-samples-component')) {
  customElements.define('complimentary-samples-component', ComplimentarySamplesComponent);
}
